import {
  Box,
  Button,
  Flex,
  Loader,
  Table,
  Text,
  Title,
  Alert
} from '@mantine/core';
import { useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import OrderStatuses from '../../enum/OrderStatuses';
import { useGetOrders } from '../../hooks/orders/queries';
import { buildOrderFolio } from '../../utils/ordersUtils';
import { formatMoneyString } from '../../utils/strings';
import { getCustomerFullName } from '../../utils/strings';

type Props = {
  storeId: string;
  customerId: string;
  highlightOrderId?: string;
  customerName?: string;
  customerPhone?: string;
  onSelectOrder: (orderId: string) => void;
  onClose: () => void;
};

export function CustomerScannedModalContent({
  storeId,
  customerId,
  highlightOrderId,
  customerName,
  customerPhone,
  onSelectOrder,
  onClose
}: Props) {
  const { t } = useTranslation(['ScanOrderModal', 'FieldName']);
  const highlightRef = useRef<HTMLTableRowElement | null>(null);

  const query = useGetOrders(!!storeId && !!customerId, {
    storeId,
    customerId,
    limit: 25,
    pageNum: 0,
    sequenceSort: '-1'
  });

  const orders = useMemo(
    () => query.data?.pages?.flatMap((page) => page.orders) || [],
    [query.data]
  );

  const fallbackName =
    orders[0]?.customer &&
    getCustomerFullName(orders[0]?.customer?.name, orders[0]?.customer?.lastName);
  const fallbackPhone = orders[0]?.customer?.phone;
  const displayName = customerName || fallbackName || customerId;
  const displayPhone = customerPhone || fallbackPhone;
  const displayCustomerNumber = orders[0]?.customer?.customerNumber;

  const highlightNotFound = !!highlightOrderId && !orders.some((o) => o._id === highlightOrderId);

  useEffect(() => {
    if (highlightRef.current) {
      highlightRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [highlightOrderId, orders.length]);

  return (
    <Box mt="md">
      <Flex justify="space-between" align="center" mb="sm">
        <div>
          <Title order={4}>{t('ScanOrderModal:customerOrdersTitle')}</Title>
          <Text size="sm" c="dimmed">
            {t('ScanOrderModal:customerOrdersSubtitle', { customerId: `${displayCustomerNumber} - ${displayName}` })}
          </Text>
          {displayPhone && (
            <Text size="sm" c="dimmed">
              {t('FieldName:phone')}: {displayPhone}
            </Text>
          )}
        </div>
        <Flex gap="xs">
          <Button variant="subtle" size="xs" onClick={() => query.refetch()}>
            {t('CommonButtons:refresh') || 'Refresh'}
          </Button>
          <Button variant="light" size="xs" onClick={onClose}>
            {t('CommonButtons:close')}
          </Button>
        </Flex>
      </Flex>

      {query.isFetching ? (
        <Flex justify="center" align="center" h={120} direction="column">
          <Loader />
          <Text mt="xs">{t('ScanOrderModal:searchingOrder')}</Text>
        </Flex>
      ) : orders.length === 0 ? (
        <Text>{t('ScanOrderModal:customerOrdersEmpty')}</Text>
      ) : (
        <Table striped highlightOnHover withColumnBorders withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t('FieldName:order')}</Table.Th>
              <Table.Th>{t('FieldName:status')}</Table.Th>
              <Table.Th>{t('FieldName:total')}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {orders.map((order: any) => (
              <Table.Tr
                key={order._id}
                ref={order._id === highlightOrderId ? highlightRef : undefined}
                style={{
                  background:
                    order._id === highlightOrderId ? 'var(--mantine-color-blue-0)' : undefined,
                  border: order._id === highlightOrderId ? '2px solid var(--mantine-color-blue-4)' : undefined,
                  cursor: 'pointer'
                }}
                onClick={() => onSelectOrder(order._id)}>
                <Table.Td>{buildOrderFolio(order?.folioPrefix, order?.sequence)}</Table.Td>
                <Table.Td>
                  {t(
                    `FieldName:orderStatuses.${
                      OrderStatuses[order?.status?.toUpperCase() as keyof typeof OrderStatuses]
                    }`
                  )}
                </Table.Td>
                <Table.Td>{formatMoneyString(order.total)}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
      {highlightNotFound && (
        <Alert mt="md" color="yellow" title={t('ScanOrderModal:highlightNotFoundTitle')}>
          {t('ScanOrderModal:highlightNotFound', { orderId: highlightOrderId })}
        </Alert>
      )}
    </Box>
  );
}
